import { createTheme, PaletteMode } from '@mui/material';

const vinoskColors = {
  primaryPurple: '#641835',
  wineRed: '#722F37',
  wineWhite: '#F7F4E9',
  champagne: '#F7E7CE',
  rose: '#E8A4C0',
  darkBackground: '#121212',
  darkSurface: '#1E1E1E',
  darkSurfaceVariant: '#2A2A2A',
  textPrimary: '#FFFFFF',
  textSecondary: '#B3B3B3',
  divider: '#424242',
  error: '#CF6679',
  success: '#66BB6A',
  warning: '#FFA726',
  info: '#29B6F6'
};

export const createVinoskTheme = (mode: PaletteMode = 'dark') => {
  return createTheme({
    palette: {
      mode,
      primary: {
        main: vinoskColors.primaryPurple,
        light: '#8B2F54',
        dark: '#4A0E26',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: vinoskColors.wineRed,
        light: '#9A4550',
        dark: '#521923',
        contrastText: '#FFFFFF',
      },
      background: {
        default: mode === 'dark' ? vinoskColors.darkBackground : '#FFFFFF',
        paper: mode === 'dark' ? vinoskColors.darkSurface : '#F5F5F5',
      },
      text: {
        primary: mode === 'dark' ? vinoskColors.textPrimary : '#000000',
        secondary: mode === 'dark' ? vinoskColors.textSecondary : '#666666',
      },
      divider: mode === 'dark' ? vinoskColors.divider : '#E0E0E0',
      error: {
        main: vinoskColors.error,
      },
      success: {
        main: vinoskColors.success,
      },
      warning: {
        main: vinoskColors.warning,
      },
      info: {
        main: vinoskColors.info,
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: '2.5rem',
        fontWeight: 700,
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 600,
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 600,
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 500,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 500,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 500,
      },
      body1: {
        fontSize: '1rem',
      },
      body2: {
        fontSize: '0.875rem',
      },
      button: {
        textTransform: 'none',
        fontWeight: 500,
      },
    },
    shape: {
      borderRadius: 6,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '10px 24px',
            fontSize: '1rem',
            fontWeight: 500,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            },
          },
          contained: {
            '&:hover': {
              boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            boxShadow: mode === 'dark' 
              ? '0 4px 20px rgba(0,0,0,0.5)' 
              : '0 4px 20px rgba(0,0,0,0.1)',
            backgroundImage: 'none',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 6,
            },
          },
        },
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            borderRadius: '20px 20px 0 0',
            height: 70,
            backgroundColor: mode === 'dark' ? vinoskColors.darkSurfaceVariant : '#FFFFFF',
            boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
          },
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            minWidth: 0,
            padding: '6px 0',
            '&.Mui-selected': {
              color: vinoskColors.primaryPurple,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiRating: {
        styleOverrides: {
          iconFilled: {
            color: vinoskColors.champagne,
          },
          iconEmpty: {
            color: mode === 'dark' ? vinoskColors.divider : '#E0E0E0',
          },
        },
      },
    },
  });
};

export const getBottleTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    RED: vinoskColors.wineRed,
    WHITE: vinoskColors.wineWhite,
    ROSE: vinoskColors.rose,
    CHAMPAGNE: vinoskColors.champagne,
    SPIRITS: '#8B4513',
  };
  return colors[type] || vinoskColors.primaryPurple;
};